import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Profile, Recipient } from '@/lib/forward-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EditProfileDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    profile: Profile;
    recipients: Recipient[];
    onSave: (data: { title: string; receivers: string[] }) => Promise<void>;
}

export function EditProfileDialog({ open, onOpenChange, profile, recipients, onSave }: EditProfileDialogProps) {
    const { t } = useTranslation();
    const [title, setTitle] = useState(profile.title);
    const [selectedRecipients, setSelectedRecipients] = useState<string[]>(profile.receivers || []);
    const [loading, setLoading] = useState(false);

    const handleToggleRecipient = (recipientId: string) => {
        setSelectedRecipients(prev =>
            prev.includes(recipientId)
                ? prev.filter(id => id !== recipientId)
                : [...prev, recipientId]
        );
    };

    const handleSave = async () => {
        if (!title || selectedRecipients.length === 0) {
            return;
        }

        setLoading(true);
        try {
            await onSave({ title, receivers: selectedRecipients });
            onOpenChange(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('forward.editProfile')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="edit-title">{t('forward.nameYourProfile')}</Label>
                        <Input
                            id="edit-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Business Hours"
                        />
                    </div>

                    <div>
                        <Label>{t('sidebar.recipients')}</Label>
                        <p className="text-sm text-gray-600 mb-3">
                            {t('forward.selectRecipientsHint')}
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                            {recipients.map((recipient) => (
                                <div key={recipient.id} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`recipient-${recipient.id}`}
                                        checked={selectedRecipients.includes(recipient.id)}
                                        onCheckedChange={() => handleToggleRecipient(recipient.id)}
                                    />
                                    <label
                                        htmlFor={`recipient-${recipient.id}`}
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                                    >
                                        {recipient.title}
                                        <span className="text-gray-500 ml-2 text-xs">
                                            {recipient.forwardingNumber}
                                        </span>
                                    </label>
                                </div>
                            ))}
                            {recipients.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">
                                    {t('forward.noRecipientsAvailable')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            {t('global.button.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={loading || !title || selectedRecipients.length === 0}
                            className="bg-brand-primary hover:bg-brand-primary/90"
                        >
                            {loading ? t('global.button.saving') : t('global.button.saveChanges')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
